import React, { useEffect, useRef, useState } from 'react'

/**
 * A lightweight, high-performance scroll animation component for React 19.
 * Uses the native browser IntersectionObserver and hardware-accelerated CSS transitions.
 * Eliminates external animation library bundle size and React version/hook conflicts.
 */
export default function AnimateOnScroll({ 
  children, 
  variant = 'fadeInUp', // 'fadeInUp' | 'scaleIn' | 'fadeIn'
  delay = 0, 
  duration = 800, // ms
  className = '',
  as: Component = 'div'
}) {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true)
          // Once it animates in, we can stop observing
          observer.unobserve(entry.target)
        }
      },
      {
        threshold: 0.05,
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before entering fully
      }
    )

    const currentRef = ref.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [])

  // Generate CSS transition styles
  const getStyles = () => {
    const baseStyle = {
      transitionProperty: 'opacity, transform, scale',
      transitionDuration: `${duration}ms`,
      transitionDelay: `${delay}ms`,
      transitionTimingFunction: 'cubic-bezier(0.215, 0.61, 0.355, 1)', // Smooth easeOutCubic
      willChange: 'opacity, transform',
    }

    if (variant === 'fadeInUp') {
      return {
        ...baseStyle,
        opacity: isIntersecting ? 1 : 0,
        transform: isIntersecting ? 'translateY(0)' : 'translateY(24px)',
      }
    }

    if (variant === 'scaleIn') {
      return {
        ...baseStyle,
        opacity: isIntersecting ? 1 : 0,
        transform: isIntersecting ? 'scale(1)' : 'scale(0.96)',
      }
    }

    if (variant === 'fadeIn') {
      return {
        ...baseStyle,
        opacity: isIntersecting ? 1 : 0,
      }
    }

    return baseStyle
  }

  return (
    <Component
      ref={ref}
      style={getStyles()}
      className={className}
    >
      {children}
    </Component>
  )
}
